import React, { useCallback, useRef } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ButtonBase, Popover, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { AllergiesKind, FoodItemFragment } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";

import FoodSelectContent from "./FoodSelectContent";
import If from "../layout/If";

interface Props {
  placeholder?: string;
  value?: FoodItemFragment;
  onChange?: (value: FoodItemFragment) => void;
  clientAllergies?: AllergiesKind[];
}

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
  },
  button: {
    flex: 1,
    border: "solid",
    borderColor: theme.palette.grey[200],
    borderWidth: "1px",
    padding: theme.spacing(1.35, 4),
    color: colors.dark,
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    "& MuiButtonBase-root > p": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  placeholder: {
    color: colors.gray,
  },
}));

const FoodSelect: React.FC<Props> = ({
  placeholder,
  value,
  onChange = () => {},
  clientAllergies = [],
}) => {
  const { classes } = useStyles();
  const popover = useDialogState();
  const anchorRef = useRef(null);

  const handleSelect = useCallback(
    (value: FoodItemFragment) => {
      onChange(value);
      popover.hide();
    },
    [onChange]
  );

  return (
    <div className={classes.root}>
      <ButtonBase
        ref={anchorRef}
        className={classes.button}
        onClick={popover.toggle}
      >
        <If
          condition={value !== undefined}
          then={<Typography noWrap>{value?.name}</Typography>}
          else={
            <Typography className={classes.placeholder} noWrap>
              {placeholder}
            </Typography>
          }
        />
        <KeyboardArrowDownIcon htmlColor={colors.gray} />
      </ButtonBase>
      <Popover
        anchorEl={anchorRef.current}
        open={popover.shown}
        onClose={popover.hide}
        anchorOrigin={{
          horizontal: "center",
          vertical: "center",
        }}
        transformOrigin={{
          horizontal: "center",
          vertical: "center",
        }}
      >
        <FoodSelectContent
          onSelect={handleSelect}
          clientAllergies={clientAllergies}
        />
      </Popover>
    </div>
  );
};

export default FoodSelect;
